/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  Switch,
  Alert,
  Collapse,
  CircularProgress,
  Stack,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Schedule as ScheduleIcon,
  SmartToy as AgentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  DoNotDisturb as SkippedIcon,
  OpenInNew as OpenInNewIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { agentScheduleApi, agentApi } from '../../services/api';
import { useAiDateTimeFormatting } from '../../hooks/useAiDateTimeFormatting';
import { AgentFormDialog } from '../agent/AgentFormDialog';
import { AI_ACTION_SURFACES } from '../../../access/surfaces';
import { useActionAccess } from '../../../access/actionAccess';
import type { 
  AgentSchedule, 
  AgentScheduleCreate, 
  AgentScheduleUpdate,
  AgentScheduleType,
  AgentScheduleRecurringUnit,
  AgentScheduleWeekDay,
  AgentScheduleExecution,
  AgentScheduleTimeRange,
  AgentListItem,
} from '../../types/agent';

interface AgentSchedulePanelProps {
  systemId: number;
  agentId?: number; // Optional: filter by specific agent
}

interface ScheduleFormData {
  name: string;
  description: string;
  agent: number | '';
  task_prompt: string;
  schedule_type: AgentScheduleType;
  start_at: string;
  end_at: string | null;
  recurring_unit: AgentScheduleRecurringUnit | null;
  recurring_value: number | null;
  excluded_days: AgentScheduleWeekDay[] | null;
  excluded_time_ranges: AgentScheduleTimeRange[] | null;
  max_iterations: number;
  timeout_seconds: number;
  is_active: boolean;
}

const WEEKDAYS: { value: AgentScheduleWeekDay; label: string }[] = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
];

const RECURRING_UNITS: { value: AgentScheduleRecurringUnit; label: string }[] = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
];

const getStatusIcon = (status: string | null) => {
  switch (status) {
    case 'SUCCESS':
      return <SuccessIcon fontSize="small" color="success" />;
    case 'ERROR':
      return <ErrorIcon fontSize="small" color="error" />;
    case 'RUNNING':
    case 'PENDING':
      return <PendingIcon fontSize="small" color="warning" />;
    case 'SKIPPED':
      return <SkippedIcon fontSize="small" color="disabled" />;
    default:
      return null;
  }
};

const getStatusColor = (status: string | null): 'success' | 'error' | 'warning' | 'default' => {
  switch (status) {
    case 'SUCCESS':
      return 'success';
    case 'ERROR':
      return 'error';
    case 'RUNNING':
    case 'PENDING':
      return 'warning';
    default:
      return 'default';
  }
};

export const AgentSchedulePanel: React.FC<AgentSchedulePanelProps> = ({ systemId, agentId }) => {
  const queryClient = useQueryClient();
  const scheduleTriggerAccess = useActionAccess(AI_ACTION_SURFACES.scheduleTrigger);
  const { formatDateTime, toDateTimeLocalValue, fromDateTimeLocalValue } = useAiDateTimeFormatting();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [formOpen, setFormOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<AgentSchedule | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<AgentSchedule | null>(null);
  const [expandedSchedule, setExpandedSchedule] = useState<number | null>(null);
  const [triggeringId, setTriggeringId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [loadingScheduleDetails, setLoadingScheduleDetails] = useState(false);
  const [agentDetailOpen, setAgentDetailOpen] = useState(false);
  const [selectedAgentForDetail, setSelectedAgentForDetail] = useState<AgentListItem | null>(null);
  const [createAgentDialogOpen, setCreateAgentDialogOpen] = useState(false);

  const [formData, setFormData] = useState<ScheduleFormData>({
    name: '',
    description: '',
    agent: '',
    task_prompt: '',
    schedule_type: 'RECURRING',
    start_at: '',
    end_at: null,
    recurring_unit: 'hours',
    recurring_value: 1,
    excluded_days: ['SATURDAY', 'SUNDAY'],
    excluded_time_ranges: null,
    max_iterations: 15,
    timeout_seconds: 300,
    is_active: true,
  });

  // Fetch agents for dropdown - get all user's active agents (including templates)
  const { data: agentsData } = useQuery({
    queryKey: ['agents-for-schedule'],
    queryFn: () => agentApi.list(1, 500, { is_active: true }),
  });

  // Agents are already filtered server-side
  const agents = agentsData?.results || [];

  // Fetch agent schedules
  const { data: schedules, isLoading } = useQuery({
    queryKey: ['agent-schedules', systemId, agentId],
    queryFn: () => agentScheduleApi.list({
      ai_system_id: systemId,
      agent_id: agentId,
    }),
    enabled: !!systemId,
  });

  // Create schedule mutation
  const createMutation = useMutation({
    mutationFn: (data: AgentScheduleCreate) => agentScheduleApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-schedules', systemId] });
      handleCloseForm();
    },
  });

  // Update schedule mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AgentScheduleUpdate }) =>
      agentScheduleApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-schedules', systemId] });
      handleCloseForm();
    },
  });

  // Delete schedule mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => agentScheduleApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-schedules', systemId] });
      setDeleteConfirmOpen(false);
      setScheduleToDelete(null);
    },
  });

  // Toggle active mutation
  const toggleMutation = useMutation({
    mutationFn: (id: number) => agentScheduleApi.toggle(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['agent-schedules', systemId] });
      const previous = queryClient.getQueryData(['agent-schedules', systemId]);
      queryClient.setQueryData(['agent-schedules', systemId], (old: any) => {
        if (!old?.results) return old;
        return {
          ...old,
          results: old.results.map((s: AgentSchedule) =>
            s.id === id ? { ...s, is_active: !s.is_active } : s
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['agent-schedules', systemId], context.previous);
      }
      setTogglingId(null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-schedules', systemId] });
      setTogglingId(null);
    },
  });

  // Trigger schedule mutation
  const triggerMutation = useMutation({
    mutationFn: (id: number) => agentScheduleApi.trigger(id, false),
    onSuccess: (data) => {
      setTriggeringId(null);
      queryClient.invalidateQueries({ queryKey: ['agent-schedules', systemId] });
      // Could show a snackbar with data.message
    },
    onError: () => {
      setTriggeringId(null);
    },
  });

  // Fetch executions for expanded schedule
  const { data: executions } = useQuery({
    queryKey: ['agent-schedule-executions', expandedSchedule],
    queryFn: () => agentScheduleApi.getExecutions(expandedSchedule!, undefined, 10),
    enabled: !!expandedSchedule,
  });

  const handleOpenForm = async (schedule?: AgentSchedule) => {
    if (schedule) {
      // Fetch full schedule details to get all fields (list endpoint doesn't include all)
      setLoadingScheduleDetails(true);
      setFormOpen(true);
      try {
        const fullSchedule = await agentScheduleApi.get(schedule.id);
        setEditingSchedule(fullSchedule);
        setFormData({
          name: fullSchedule.name,
          description: fullSchedule.description || '',
          agent: fullSchedule.agent,
          task_prompt: fullSchedule.task_prompt || '',
          schedule_type: fullSchedule.schedule_type,
          start_at: toDateTimeLocalValue(fullSchedule.start_at),
          end_at: fullSchedule.end_at ? toDateTimeLocalValue(fullSchedule.end_at) : null,
          recurring_unit: fullSchedule.recurring_unit,
          recurring_value: fullSchedule.recurring_value,
          excluded_days: fullSchedule.excluded_days,
          excluded_time_ranges: fullSchedule.excluded_time_ranges,
          max_iterations: fullSchedule.max_iterations || 15,
          timeout_seconds: fullSchedule.timeout_seconds || 300,
          is_active: fullSchedule.is_active,
        });
      } catch (error) {
        console.error('Failed to load schedule details:', error);
        // Fallback to partial data from list
        setEditingSchedule(schedule);
        setFormData({
          name: schedule.name,
          description: schedule.description || '',
          agent: schedule.agent,
          task_prompt: schedule.task_prompt || '',
          schedule_type: schedule.schedule_type,
          start_at: toDateTimeLocalValue(schedule.start_at),
          end_at: schedule.end_at ? toDateTimeLocalValue(schedule.end_at) : null,
          recurring_unit: schedule.recurring_unit,
          recurring_value: schedule.recurring_value,
          excluded_days: schedule.excluded_days || null,
          excluded_time_ranges: schedule.excluded_time_ranges || null,
          max_iterations: schedule.max_iterations || 15,
          timeout_seconds: schedule.timeout_seconds || 300,
          is_active: schedule.is_active,
        });
      } finally {
        setLoadingScheduleDetails(false);
      }
    } else {
      setEditingSchedule(null);
      // Auto-select the first AI System Builder agent (there should typically be one)
      const defaultAgent = agents.length > 0 ? agents[0].id : '';
      setFormData({
        name: '',
        description: '',
        agent: defaultAgent,
        task_prompt: 'Analyze the trading performance of this AI System and provide optimization recommendations.',
        schedule_type: 'RECURRING',
        start_at: '',
        end_at: null,
        recurring_unit: 'hours',
        recurring_value: 24,
        excluded_days: ['SATURDAY', 'SUNDAY'],
        excluded_time_ranges: null,
        max_iterations: 15,
        timeout_seconds: 300,
        is_active: true,
      });
      setFormOpen(true);
    }
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingSchedule(null);
  };

  const handleSubmit = () => {
    if (formData.agent === '') return;

    const scheduleData: AgentScheduleCreate = {
      name: formData.name,
      description: formData.description,
      agent: formData.agent as number,
      task_prompt: formData.task_prompt,
      target_ai_system: systemId,
      schedule_type: formData.schedule_type,
      start_at: fromDateTimeLocalValue(formData.start_at) || new Date().toISOString(),
      end_at: fromDateTimeLocalValue(formData.end_at),
      recurring_unit: formData.recurring_unit,
      recurring_value: formData.recurring_value,
      excluded_days: formData.excluded_days,
      excluded_time_ranges: formData.excluded_time_ranges,
      max_iterations: formData.max_iterations,
      timeout_seconds: formData.timeout_seconds,
      is_active: formData.is_active,
    };

    if (editingSchedule) {
      updateMutation.mutate({ id: editingSchedule.id, data: scheduleData });
    } else {
      createMutation.mutate(scheduleData);
    }
  };

  const handleDelete = (schedule: AgentSchedule) => {
    setScheduleToDelete(schedule);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (scheduleToDelete) {
      deleteMutation.mutate(scheduleToDelete.id);
    }
  };

  const handleTrigger = (id: number) => {
    if (!scheduleTriggerAccess.ensureAccess()) return;
    setTriggeringId(id);
    triggerMutation.mutate(id);
  };

  const formatRecurring = (schedule: AgentSchedule) => {
    if (schedule.schedule_type === 'ONE_TIME') {
      return 'One-time';
    }
    if (schedule.recurring_value && schedule.recurring_unit) {
      return `Every ${schedule.recurring_value} ${schedule.recurring_unit}`;
    }
    return 'Recurring';
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <AgentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            AI Manager Schedules
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()}
            disabled={agents.length === 0}
          >
            Add Schedule
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Schedule the AI Manager to automatically analyze this system's performance 
          and provide optimization recommendations on a regular basis.
        </Alert>

        {agents.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No AI Manager agent found. Create an AI System Builder agent first.
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : schedules?.results && schedules.results.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40}></TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Agent</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Last Run</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Runs</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.results.map((schedule) => (
                  <React.Fragment key={schedule.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => setExpandedSchedule(
                            expandedSchedule === schedule.id ? null : schedule.id
                          )}
                        >
                          {expandedSchedule === schedule.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {schedule.name}
                        </Typography>
                        {schedule.description && (
                          <Typography variant="caption" color="text.secondary">
                            {schedule.description.substring(0, 50)}
                            {schedule.description.length > 50 ? '...' : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={schedule.agent_name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatRecurring(schedule)}
                          size="small"
                          color={schedule.schedule_type === 'RECURRING' ? 'info' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <span>{formatRelativeTime(schedule.last_execution_at)}</span>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getStatusIcon(schedule.last_execution_status)}
                          <Chip
                            label={schedule.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={schedule.is_active ? 'success' : 'default'}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={schedule.execution_count}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleTrigger(schedule.id)}
                            disabled={!schedule.is_active || triggeringId === schedule.id || scheduleTriggerAccess.disabled}
                            title={scheduleTriggerAccess.disabled ? scheduleTriggerAccess.blockedMessage : 'Run now'}
                          >
                            {triggeringId === schedule.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <PlayIcon />
                            )}
                          </IconButton>
                        </span>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTogglingId(schedule.id);
                            toggleMutation.mutate(schedule.id);
                          }}
                          color={schedule.is_active ? 'warning' : 'success'}
                          disabled={togglingId === schedule.id}
                        >
                          {togglingId === schedule.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            schedule.is_active ? <PauseIcon /> : <PlayIcon />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenForm(schedule)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(schedule)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {/* Expanded row for executions */}
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0 }}>
                        <Collapse in={expandedSchedule === schedule.id}>
                          <Box sx={{ p: 2, bgcolor: isDark ? 'grey.900' : 'grey.50' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              <HistoryIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                              Task Prompt
                            </Typography>
                            <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'background.paper' }}>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {schedule.task_prompt}
                              </Typography>
                            </Paper>

                            <Typography variant="subtitle2" gutterBottom>
                              Recent Executions
                            </Typography>
                            {executions && executions.length > 0 ? (
                              <Stack spacing={1}>
                                {executions.map((exec: AgentScheduleExecution) => (
                                  <Paper key={exec.id} sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {getStatusIcon(exec.status)}
                                        <Chip
                                          label={exec.status}
                                          size="small"
                                          color={getStatusColor(exec.status)}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                          {formatDateTime(exec.started_at)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          Tools: {exec.tool_calls_count}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Tokens: {exec.tokens_used}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    {exec.error && (
                                      <Alert severity="error" sx={{ mt: 1 }}>
                                        {exec.error}
                                      </Alert>
                                    )}
                                    {exec.response && (
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          mt: 1, 
                                          maxHeight: 100, 
                                          overflow: 'auto',
                                          whiteSpace: 'pre-wrap',
                                          fontSize: '0.8rem',
                                        }}
                                      >
                                        {exec.response.substring(0, 500)}
                                        {exec.response.length > 500 ? '...' : ''}
                                      </Typography>
                                    )}
                                  </Paper>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No executions yet
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No agent schedules configured
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Create a schedule to have the AI Manager automatically analyze
              this system and provide insights.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
              disabled={agents.length === 0}
            >
              Create First Schedule
            </Button>
          </Paper>
        )}
      </CardContent>

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSchedule ? 'Edit Agent Schedule' : 'Create Agent Schedule'}
        </DialogTitle>
        <DialogContent>
          {loadingScheduleDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading schedule details...</Typography>
            </Box>
          ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Schedule Name"
                fullWidth
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Daily Performance Analysis"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description of what this schedule does"
              />
            </Grid>

            {/* Agent selector with shortcuts */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <FormControl fullWidth required>
                  <InputLabel>Agent</InputLabel>
                  <Select
                    value={formData.agent}
                    onChange={(e) => setFormData({ ...formData, agent: e.target.value as number })}
                    label="Agent"
                    startAdornment={
                      <AgentIcon sx={{ ml: 1, mr: 0.5, color: 'action.active' }} />
                    }
                  >
                    {agents.length === 0 ? (
                      <MenuItem value="" disabled>
                        No AI System Builder agents available
                      </MenuItem>
                    ) : (
                      agents.map((agent: AgentListItem) => (
                        <MenuItem key={agent.id} value={agent.id}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2">{agent.name}</Typography>
                            {agent.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {agent.description}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const agent = agents.find((a: AgentListItem) => a.id === formData.agent);
                      if (agent) {
                        setSelectedAgentForDetail(agent);
                        setAgentDetailOpen(true);
                      }
                    }}
                    disabled={!formData.agent}
                    sx={{ mt: 1 }}
                  >
                    <ViewIcon />
                  </IconButton>
                </span>
                <IconButton
                  size="small"
                  onClick={() => setCreateAgentDialogOpen(true)}
                  sx={{ mt: 1 }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Select an AI System Builder agent to run scheduled analysis tasks
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Task Prompt"
                fullWidth
                required
                multiline
                rows={4}
                value={formData.task_prompt}
                onChange={(e) => setFormData({ ...formData, task_prompt: e.target.value })}
                helperText="The task/question to ask the AI Manager when the schedule triggers"
                placeholder="Analyze the trading performance of this AI System for the past week and provide specific optimization recommendations."
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }}>Schedule Configuration</Divider>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Schedule Type</InputLabel>
                <Select
                  value={formData.schedule_type}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    schedule_type: e.target.value as AgentScheduleType 
                  })}
                  label="Schedule Type"
                >
                  <MenuItem value="ONE_TIME">One Time</MenuItem>
                  <MenuItem value="RECURRING">Recurring</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Start At"
                type="datetime-local"
                fullWidth
                required
                value={formData.start_at}
                onChange={(e) => setFormData({ ...formData, start_at: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {formData.schedule_type === 'RECURRING' && (
              <>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField
                    label="Every"
                    type="number"
                    fullWidth
                    value={formData.recurring_value || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      recurring_value: parseInt(e.target.value) || null 
                    })}
                    inputProps={{ min: 1 }}
                  />
                </Grid>

                <Grid size={{ xs: 6, sm: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={formData.recurring_unit || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        recurring_unit: e.target.value as AgentScheduleRecurringUnit 
                      })}
                      label="Unit"
                    >
                      {RECURRING_UNITS.map((unit) => (
                        <MenuItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="End At (Optional)"
                    type="datetime-local"
                    fullWidth
                    value={formData.end_at || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      end_at: e.target.value || null 
                    })}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave empty for no end date"
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel>Excluded Days</InputLabel>
                    <Select
                      multiple
                      value={formData.excluded_days || []}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        excluded_days: e.target.value as AgentScheduleWeekDay[] 
                      })}
                      label="Excluded Days"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {WEEKDAYS.map((day) => (
                        <MenuItem key={day.value} value={day.value}>
                          {day.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }}>Execution Settings</Divider>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <TextField
                label="Max Iterations"
                type="number"
                fullWidth
                value={formData.max_iterations}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  max_iterations: parseInt(e.target.value) || 10 
                })}
                inputProps={{ min: 1, max: 50 }}
                helperText="Max tool calls per execution"
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <TextField
                label="Timeout (seconds)"
                type="number"
                fullWidth
                value={formData.timeout_seconds}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  timeout_seconds: parseInt(e.target.value) || 300 
                })}
                inputProps={{ min: 60, max: 1800 }}
                helperText="Execution timeout"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Schedule Active"
              />
            </Grid>
          </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              loadingScheduleDetails ||
              createMutation.isPending || 
              updateMutation.isPending ||
              !formData.name ||
              !formData.agent ||
              !formData.task_prompt ||
              !formData.start_at
            }
          >
            {editingSchedule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Schedule</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the schedule "{scheduleToDelete?.name}"?
            This will also delete all execution history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Agent Detail Dialog */}
      <Dialog 
        open={agentDetailOpen} 
        onClose={() => setAgentDetailOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AgentIcon color="primary" />
          Agent Details: {selectedAgentForDetail?.name}
        </DialogTitle>
        <DialogContent dividers>
          {selectedAgentForDetail && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1">{selectedAgentForDetail.name}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                <Chip 
                  label={selectedAgentForDetail.agent_type} 
                  size="small" 
                  color={selectedAgentForDetail.agent_type === 'AI_SYSTEM_BUILDER' ? 'primary' : 'default'}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography variant="body2">
                  {selectedAgentForDetail.description || 'No description provided'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Model</Typography>
                <Typography variant="body2">{selectedAgentForDetail.model_name || 'Not specified'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip 
                  label={selectedAgentForDetail.is_active ? 'Active' : 'Inactive'} 
                  size="small" 
                  color={selectedAgentForDetail.is_active ? 'success' : 'default'}
                />
              </Grid>
              {selectedAgentForDetail.capabilities && selectedAgentForDetail.capabilities.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Capabilities</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedAgentForDetail.capabilities.map((cap: string, idx: number) => (
                      <Chip key={idx} label={cap} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              )}
              {selectedAgentForDetail.tool_count !== undefined && selectedAgentForDetail.tool_count > 0 && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Assigned Tools</Typography>
                  <Typography variant="body2">{selectedAgentForDetail.tool_count} tools</Typography>
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                <Typography variant="body2">
                  {selectedAgentForDetail.created_at 
                    ? formatDateTime(selectedAgentForDetail.created_at)
                    : 'Unknown'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgentDetailOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={() => {
              if (selectedAgentForDetail) {
                window.open(`/agents/${selectedAgentForDetail.id}`, '_blank');
              }
            }}
          >
            Open Full View
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Agent Dialog */}
      <AgentFormDialog
        open={createAgentDialogOpen}
        onClose={() => setCreateAgentDialogOpen(false)}
        onSuccess={(newAgent) => {
          // Refresh agents list and select the newly created agent
          queryClient.invalidateQueries({ queryKey: ['agents'] });
          if (newAgent?.id) {
            setFormData((prev) => ({ ...prev, agent: newAgent.id }));
          }
          setCreateAgentDialogOpen(false);
        }}
      />
    </Card>
  );
};

export default AgentSchedulePanel;
